import React, { FC, useState, useEffect } from 'react';
import 'react-dates/initialize';
import {
  DayPickerRangeController,
  type FocusedInputShape,
  isSameDay,
} from 'react-dates';
import { type Moment } from 'moment/moment';
import moment from 'moment';
import { Menu, MenuItem } from '../../menu';
import { Input, InputProps } from '../../input';
import { Button, IconButton } from '../../buttons';
import { Stack } from '../../stack';
import { Icon } from '../../icon';
import { TextOverline } from '../../text';
import { Scrollbar } from '../../scrollbar';
import './range-datepicker.scss';
import { Tooltip } from '../../tooltip';
import { addLeadingZeros, formatDateInput, formatYear } from '../utils';
import { Toggle } from '../../toggle';

const INPUT_FORMAT = 'MM/DD/YYYY';
const TIME_FORMAT = 'HH:mm:ss';
const ERROR_MESSAGE = 'Invalid date';
const TIME_ERROR_MESSAGE = 'Invalid time';
const TIME_RANGE_ERROR_MESSAGE = 'End time must be after start time';

const renderNavPrevButton = ({
  ariaLabel,
  disabled,
  onClick,
}: {
  ariaLabel?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
}) => (
  <IconButton
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={onClick}
    style={{ position: 'absolute', top: -4, left: 24 }}
    name="chevron-left"
    size="small"
  />
);

const renderNavNextButton = ({
  ariaLabel,
  disabled,
  onClick,
}: {
  ariaLabel?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
}) => (
  <IconButton
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={onClick}
    style={{ position: 'absolute', top: -4, right: 18 }}
    name="chevron-right"
    size="small"
  />
);

const renderMonthText = (month: Moment) => (
  <TextOverline variant="primary" textAlign="center">
    {moment(month).format('MMMM YYYY')}
  </TextOverline>
);

export type RangeDatepickerPresetProps = {
  text: string;
  start: Moment;
  end: Moment;
  id: string;
};

const today = moment();
const yesterday = moment().subtract(1, 'day');
const defaultPresets = [
  {
    text: 'Today',
    start: today,
    end: today,
    id: 'dStart',
  },
  {
    text: 'Yesterday',
    start: yesterday,
    end: yesterday,
    id: '-1dStart',
  },
  {
    text: 'Last 24 hours',
    start: moment().subtract(1, 'day'),
    end: today,
    id: '-24h',
  },
  {
    text: 'Last 7 days',
    start: moment().subtract(6, 'day'),
    end: today,
    id: '-7d',
  },
  {
    text: 'Last 14 days',
    start: moment().subtract(13, 'day'),
    end: today,
    id: '-14d',
  },
  {
    text: 'Last 30 days',
    start: moment().subtract(29, 'day'),
    end: today,
    id: '-30d',
  },
  {
    text: 'Last 90 days',
    start: moment().subtract(89, 'day'),
    end: today,
    id: '-90d',
  },
];

interface RangeDatepickerProps {
  presets?: RangeDatepickerPresetProps[];
  omitDefaultPresets?: boolean;
  startDate?: Moment | null;
  endDate?: Moment | null;
  selectedPreset?: string | null;
  onDatesChange?: ({
    startDate,
    endDate,
  }: {
    startDate: Moment | null;
    endDate: Moment | null;
    id?: RangeDatepickerPresetProps['id'];
  }) => void;
  onCancel?: () => void;
  onSubmit?: ({
    startDate,
    endDate,
    id,
  }: {
    startDate: Moment | null;
    endDate: Moment | null;
    id?: RangeDatepickerPresetProps['id'];
  }) => void;
  showLockToggle?: boolean;
  locked?: boolean;
  onLockedChange?: (locked: boolean) => void;
  showTimestamp?: boolean;
  timestamp?: boolean;
  onTimestampChange?: (timestamp: boolean) => void;
  startTime?: string;
  endTime?: string;
  timezone?: string;
}

export const RangeDatepicker: FC<RangeDatepickerProps> = ({
  presets,
  omitDefaultPresets = false,
  startDate: initialStartDate = null,
  endDate: initialEndDate = null,
  selectedPreset: initialSelectedPreset = null,
  onDatesChange,
  onCancel,
  onSubmit,
  showLockToggle = false,
  locked,
  onLockedChange,
  showTimestamp = false,
  timestamp = false,
  onTimestampChange,
  startTime: initialStartTime,
  endTime: initialEndTime,
  timezone,
}) => {
  const getInitialStartTime = () => {
    if (initialStartTime !== undefined) {
      return initialStartTime;
    }
    if (initialStartDate) {
      return moment(initialStartDate).format(TIME_FORMAT);
    }
    return '';
  };

  const getInitialEndTime = () => {
    if (initialEndTime !== undefined) {
      return initialEndTime;
    }
    if (initialEndDate) {
      return moment(initialEndDate).format(TIME_FORMAT);
    }
    return '';
  };

  const [startDate, setStartDate] = React.useState<Moment | null>(
    initialStartDate
  );
  const [endDate, setEndDate] = React.useState<Moment | null>(initialEndDate);
  const [filterKey, setFilterKey] = React.useState<
    RangeDatepickerPresetProps['id'] | undefined
  >();
  const [focusedInput, setFocusedInput] =
    React.useState<FocusedInputShape>('startDate');
  const [selectedPreset, setSelectedPreset] = useState<string | null>(
    initialSelectedPreset
  );
  const [dateDifference, setDateDifference] = React.useState<number>(0);
  const [hasErrorStartDate, setHasErrorStartDate] =
    React.useState<boolean>(false);
  const [hasErrorEndDate, setHasErrorEndDate] = React.useState<boolean>(false);
  const [rawStartDate, setRawStartDate] = React.useState<string>(
    startDate ? moment(startDate).format(INPUT_FORMAT) : ''
  );
  const [rawEndDate, setRawEndDate] = useState<string>(
    endDate ? moment(endDate).format(INPUT_FORMAT) : ''
  );
  const [startTime, setStartTime] = React.useState<string>(
    getInitialStartTime()
  );
  const [endTime, setEndTime] = React.useState<string>(getInitialEndTime());
  const [hasErrorStartTime, setHasErrorStartTime] =
    React.useState<boolean>(false);
  const [hasErrorEndTime, setHasErrorEndTime] = React.useState<boolean>(false);
  const [hasErrorTimeRange, setHasErrorTimeRange] =
    React.useState<boolean>(false);
  const [internalTimestamp, setInternalTimestamp] =
    React.useState<boolean>(timestamp);

  const isPresetClick = React.useRef<boolean>(false);

  const resetAllErrors = React.useCallback(() => {
    setHasErrorStartDate(false);
    setHasErrorEndDate(false);
    setHasErrorStartTime(false);
    setHasErrorEndTime(false);
    setHasErrorTimeRange(false);
  }, []);

  const validateTimeRange = React.useCallback(() => {
    if (!internalTimestamp || !startDate || !endDate) {
      setHasErrorTimeRange(false);
      return;
    }

    if (isSameDay(startDate, endDate)) {
      const startMoment = moment(startTime, TIME_FORMAT, true);
      const endMoment = moment(endTime, TIME_FORMAT, true);

      if (startMoment.isValid() && endMoment.isValid()) {
        if (endMoment.isSameOrBefore(startMoment)) {
          setHasErrorTimeRange(true);
          return;
        }
      }
    }

    setHasErrorTimeRange(false);
  }, [internalTimestamp, startDate, endDate, startTime, endTime]);

  React.useEffect(() => {
    setInternalTimestamp(timestamp);
  }, [timestamp]);

  React.useEffect(() => {
    if (internalTimestamp && (!startTime || !endTime)) {
      if (!startTime) setStartTime('00:00:00');
      if (!endTime) setEndTime('23:59:59');
    }
  }, [internalTimestamp, startTime, endTime]);

  useEffect(() => {
    if (startDate && endDate) {
      setDateDifference(endDate.diff(startDate, 'days'));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    validateTimeRange();
  }, [validateTimeRange]);

  useEffect(() => {
    if (selectedPreset && internalTimestamp) {
      setInternalTimestamp(false);
      onTimestampChange?.(false);
    }
  }, [selectedPreset, internalTimestamp, onTimestampChange]);

  const getTimeHelpText = (hasTimeError: boolean, hasRangeError: boolean) => {
    if (hasTimeError) {
      return TIME_ERROR_MESSAGE;
    }
    if (hasRangeError) {
      return TIME_RANGE_ERROR_MESSAGE;
    }
    return '';
  };

  const onChange = ({
    startDate: start,
    endDate: end,
    id,
  }: {
    startDate: Moment | null;
    endDate: Moment | null;
    id?: RangeDatepickerPresetProps['id'];
  }) => {
    let finalStartDate = start;
    let finalEndDate = end;

    if (internalTimestamp && start && end) {
      setStartTime('00:00:00');
      setEndTime('23:59:59');

      finalStartDate = moment(start).startOf('day');
      finalEndDate = moment(end).endOf('day');
    }

    setStartDate(finalStartDate);
    setEndDate(finalEndDate);
    setRawStartDate(start ? moment(start).format(INPUT_FORMAT) : '');
    setRawEndDate(end ? moment(end).format(INPUT_FORMAT) : '');
    resetAllErrors();
    setFilterKey(id);
    if (!isPresetClick.current) {
      setSelectedPreset(null);
    }
    isPresetClick.current = false;
    onDatesChange?.({
      startDate: finalStartDate,
      endDate: finalEndDate,
      ...(id && { id }),
    });
  };

  const handleStartDateChange: InputProps['onChange'] = (e) => {
    let value = addLeadingZeros(e.target.value);
    value = formatYear(value);
    value = formatDateInput(value);
    setRawStartDate(value);
    setSelectedPreset(null);
    const date = moment(value, INPUT_FORMAT, true);
    if (date.isValid()) {
      let finalDate = date;

      if (internalTimestamp) {
        const timeMoment = moment(startTime, TIME_FORMAT, true);
        if (timeMoment.isValid()) {
          finalDate = moment(date)
            .hour(timeMoment.hour())
            .minute(timeMoment.minute())
            .second(timeMoment.second());
        }
      }

      setStartDate(finalDate);
      setHasErrorStartDate(false);
      if (endDate && date.isAfter(endDate)) {
        setEndDate(moment(date).add(dateDifference, 'days'));
        setRawEndDate(
          moment(date).add(dateDifference, 'days').format(INPUT_FORMAT)
        );
      }
    } else {
      setHasErrorStartDate(true);
    }
  };

  const handleEndDateChange: InputProps['onChange'] = (e) => {
    let value = addLeadingZeros(e.target.value);
    value = formatYear(value);
    value = formatDateInput(value);
    setRawEndDate(value);
    setSelectedPreset(null);
    const date = moment(value, INPUT_FORMAT, true);
    if (date.isValid()) {
      let finalDate = date;

      if (internalTimestamp) {
        const timeMoment = moment(endTime, TIME_FORMAT, true);
        if (timeMoment.isValid()) {
          finalDate = moment(date)
            .hour(timeMoment.hour())
            .minute(timeMoment.minute())
            .second(timeMoment.second());
        }
      }

      setEndDate(finalDate);
      setHasErrorEndDate(false);
      if (startDate && date.isBefore(startDate)) {
        setStartDate(moment(date).subtract(dateDifference, 'days'));
        setRawStartDate(
          moment(date).subtract(dateDifference, 'days').format(INPUT_FORMAT)
        );
      }
    } else {
      setHasErrorEndDate(true);
    }
  };

  const handleStartTimeChange: InputProps['onChange'] = (e) => {
    const { value } = e.target;
    setStartTime(value);
    setSelectedPreset(null);

    const timeMoment = moment(value, TIME_FORMAT, true);
    if (timeMoment.isValid()) {
      setHasErrorStartTime(false);

      if (startDate) {
        const newStartDate = moment(startDate)
          .hour(timeMoment.hour())
          .minute(timeMoment.minute())
          .second(timeMoment.second());
        setStartDate(newStartDate);

        onDatesChange?.({
          startDate: newStartDate,
          endDate,
          ...(filterKey && { id: filterKey }),
        });
      }
    } else {
      setHasErrorStartTime(true);
    }
  };

  const handleEndTimeChange: InputProps['onChange'] = (e) => {
    const { value } = e.target;
    setEndTime(value);
    setSelectedPreset(null);

    const timeMoment = moment(value, TIME_FORMAT, true);
    if (timeMoment.isValid()) {
      setHasErrorEndTime(false);

      if (endDate) {
        const newEndDate = moment(endDate)
          .hour(timeMoment.hour())
          .minute(timeMoment.minute())
          .second(timeMoment.second());
        setEndDate(newEndDate);

        onDatesChange?.({
          startDate,
          endDate: newEndDate,
          ...(filterKey && { id: filterKey }),
        });
      }
    } else {
      setHasErrorEndTime(true);
    }
  };

  const renderDatePresets = () => {
    let presetsArr: RangeDatepickerPresetProps[] = [];

    if (!omitDefaultPresets) {
      presetsArr = defaultPresets;
    }

    if (presets) presetsArr = [...presetsArr, ...presets];

    if (presetsArr.length <= 0) return null;

    let presetsClassName = 'c-range-datepicker__presets';
    if (showLockToggle && showTimestamp) {
      presetsClassName += ' c-range-datepicker__presets--with-footer-toggles';
    } else if (showLockToggle || showTimestamp) {
      presetsClassName += ' c-range-datepicker__presets--with-footer-toggle';
    }

    return (
      <div className={presetsClassName}>
        <Scrollbar>
          <Menu>
            {presetsArr?.map(({ text, start, end, id }) => {
              let isSelected = false;

              if (startDate && endDate) {
                isSelected =
                  text === selectedPreset &&
                  isSameDay(start, startDate) &&
                  isSameDay(end, endDate);
              }

              return (
                <MenuItem
                  key={text.replace(/\s+/g, '')}
                  selected={isSelected}
                  onClick={() => {
                    setSelectedPreset(text);
                    resetAllErrors();
                    isPresetClick.current = true;
                    onChange({
                      startDate: start,
                      endDate: end,
                      id,
                    });
                  }}
                >
                  {text}
                </MenuItem>
              );
            })}
          </Menu>
        </Scrollbar>
        {showLockToggle && !showTimestamp && (
          <div className="c-range-datepicker__control-toggle">
            <Tooltip
              content="When enabled, this Insight will always use its own date range and will not be affected by the date filter on a dashboard."
              placement="top"
            >
              <Toggle
                size="small"
                checked={locked ?? false}
                onchange={(event: Event) => {
                  const target = event.target as HTMLInputElement;
                  onLockedChange?.(target.checked);
                }}
              >
                <TextOverline truncate>LOCK DATE RANGE</TextOverline>
              </Toggle>
            </Tooltip>
          </div>
        )}
        {showTimestamp && !showLockToggle && (
          <div className="c-range-datepicker__control-toggle">
            {selectedPreset ? (
              <Tooltip
                content="Timestamp is disabled when using a preset date. Select a custom date to enable timestamp"
                placement="top"
              >
                <Toggle
                  size="small"
                  checked={internalTimestamp}
                  disabled={!!selectedPreset}
                  onchange={(event: Event) => {
                    const target = event.target as HTMLInputElement;
                    const newValue = target.checked;
                    setInternalTimestamp(newValue);
                    onTimestampChange?.(newValue);
                  }}
                >
                  <TextOverline truncate>TIMESTAMP</TextOverline>
                </Toggle>
              </Tooltip>
            ) : (
              <Toggle
                size="small"
                checked={internalTimestamp}
                disabled={!!selectedPreset}
                onchange={(event: Event) => {
                  const target = event.target as HTMLInputElement;
                  const newValue = target.checked;
                  setInternalTimestamp(newValue);
                  onTimestampChange?.(newValue);
                }}
              >
                <TextOverline truncate>TIMESTAMP</TextOverline>
              </Toggle>
            )}
          </div>
        )}
        {showTimestamp && showLockToggle && (
          <div className="c-range-datepicker__control-container">
            <div>
              <Tooltip
                content="When enabled, this Insight will always use its own date range and will not be affected by the date filter on a dashboard."
                placement="top"
              >
                <Toggle
                  size="small"
                  checked={locked ?? false}
                  onchange={(event: Event) => {
                    const target = event.target as HTMLInputElement;
                    onLockedChange?.(target.checked);
                  }}
                >
                  <TextOverline truncate>LOCK DATE RANGE</TextOverline>
                </Toggle>
              </Tooltip>
            </div>
            <div>
              {selectedPreset ? (
                <Tooltip
                  content="Timestamp is disabled when using a preset date. Select a custom date to enable timestamp"
                  placement="top"
                >
                  <Toggle
                    size="small"
                    checked={internalTimestamp}
                    disabled={!!selectedPreset}
                    onchange={(event: Event) => {
                      const target = event.target as HTMLInputElement;
                      const newValue = target.checked;
                      setInternalTimestamp(newValue);
                      onTimestampChange?.(newValue);
                    }}
                  >
                    <TextOverline truncate>TIMESTAMP</TextOverline>
                  </Toggle>
                </Tooltip>
              ) : (
                <Toggle
                  size="small"
                  checked={internalTimestamp}
                  disabled={!!selectedPreset}
                  onchange={(event: Event) => {
                    const target = event.target as HTMLInputElement;
                    const newValue = target.checked;
                    setInternalTimestamp(newValue);
                    onTimestampChange?.(newValue);
                  }}
                >
                  <TextOverline truncate>TIMESTAMP</TextOverline>
                </Toggle>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const orientation = window.matchMedia('(max-width: 767px)').matches
    ? 'vertical'
    : 'horizontal';

  const isDisabledApplyButton =
    !startDate ||
    !endDate ||
    hasErrorStartDate ||
    hasErrorEndDate ||
    (internalTimestamp && (hasErrorStartTime || hasErrorEndTime)) ||
    hasErrorTimeRange;

  return (
    <div
      className={`c-range-datepicker ${showTimestamp ? 'c-range-datepicker--wide' : 'c-range-datepicker--narrow'}`}
    >
      {renderDatePresets()}
      <div className="c-range-datepicker__main">
        <div
          className={`c-range-datepicker__control ${showTimestamp ? 'c-range-datepicker__control--with-timestamp' : ''}`}
        >
          <DayPickerRangeController
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onChange}
            focusedInput={focusedInput}
            onFocusChange={(input) => {
              setFocusedInput(!input ? 'startDate' : input);
            }}
            withPortal={false}
            numberOfMonths={2}
            noBorder
            initialVisibleMonth={null}
            hideKeyboardShortcutsPanel
            orientation={orientation}
            renderNavPrevButton={renderNavPrevButton}
            renderNavNextButton={renderNavNextButton}
            renderMonthText={renderMonthText}
            daySize={40}
          />
        </div>
        <div
          className={`c-range-datepicker__controls ${showTimestamp ? 'c-range-datepicker__controls--with-timestamp' : ''} ${
            hasErrorStartDate ||
            hasErrorEndDate ||
            hasErrorStartTime ||
            hasErrorEndTime ||
            hasErrorTimeRange
              ? 'c-range-datepicker__controls--with-errors'
              : ''
          }`}
        >
          <Stack
            alignItems={showTimestamp ? 'center' : 'start'}
            gutter="small"
            justifyContent="space-between"
            nowrap
          >
            <Stack
              alignItems={showTimestamp ? 'center' : 'start'}
              justifyContent="center"
              gutter={showTimestamp ? 'xsmall' : 'small'}
              nowrap
            >
              <Input
                variant="outlined"
                style={{ width: 140 }}
                placeholder="mm/dd/yyyy"
                value={rawStartDate}
                onChange={handleStartDateChange}
                hasError={hasErrorStartDate}
                helpText={hasErrorStartDate ? ERROR_MESSAGE : ''}
                size={showTimestamp ? 'small' : 'medium'}
              >
                <Icon
                  slot="prefix"
                  name="calendar"
                  size="xsmall"
                  style={{ width: 18 }}
                />
              </Input>

              {internalTimestamp && (
                <Input
                  variant="outlined"
                  style={{ width: 115 }}
                  placeholder="hh:mm:ss"
                  value={startTime}
                  onChange={handleStartTimeChange}
                  hasError={hasErrorStartTime || hasErrorTimeRange}
                  helpText={getTimeHelpText(
                    hasErrorStartTime,
                    hasErrorTimeRange
                  )}
                  size={'small'}
                >
                  <Icon
                    slot="prefix"
                    name="clock"
                    size="xsmall"
                    style={{ width: 15 }}
                  />
                </Input>
              )}
              <Icon
                name="arrow-right"
                style={{ marginTop: showTimestamp ? 0 : 12 }}
                size="xsmall"
              />
              <Input
                variant="outlined"
                style={{ width: 140 }}
                placeholder="mm/dd/yyyy"
                value={rawEndDate}
                onChange={handleEndDateChange}
                hasError={hasErrorEndDate}
                helpText={hasErrorEndDate ? ERROR_MESSAGE : ''}
                size={showTimestamp ? 'small' : 'medium'}
              >
                <Icon
                  slot="prefix"
                  name="calendar"
                  size="xsmall"
                  style={{ width: 18 }}
                />
              </Input>
              {internalTimestamp && (
                <Input
                  variant="outlined"
                  style={{ width: 115 }}
                  placeholder="hh:mm:ss"
                  value={endTime}
                  onChange={handleEndTimeChange}
                  hasError={hasErrorEndTime || hasErrorTimeRange}
                  helpText={getTimeHelpText(hasErrorEndTime, hasErrorTimeRange)}
                  size={'small'}
                >
                  <Icon
                    slot="prefix"
                    name="clock"
                    size="xsmall"
                    style={{ width: 15 }}
                  />
                </Input>
              )}
              {showTimestamp && timezone && (
                <Tooltip
                  content="Timezone settings can be found in Manager under the Projects > Localization section."
                  placement="top"
                >
                  <TextOverline truncate>{timezone}</TextOverline>
                </Tooltip>
              )}
            </Stack>
            <Stack
              alignItems="start"
              justifyContent="end"
              gutter="small"
              nowrap
              className="c-range-datepicker__actions"
            >
              <Button
                size="large"
                variant="ghost"
                onClick={() => {
                  setStartDate(initialStartDate);
                  setEndDate(initialEndDate);
                  setRawStartDate(
                    initialStartDate
                      ? moment(initialStartDate).format(INPUT_FORMAT)
                      : ''
                  );
                  setRawEndDate(
                    initialEndDate
                      ? moment(initialEndDate).format(INPUT_FORMAT)
                      : ''
                  );
                  setFilterKey(undefined);
                  setSelectedPreset(null);
                  setStartTime(initialStartTime || '');
                  setEndTime(initialEndTime || '');
                  resetAllErrors();
                  onCancel?.();
                }}
              >
                Cancel
              </Button>
              {isDisabledApplyButton ? (
                <Tooltip content={'Please fill all inputs correctly'}>
                  <Button size="large" disabled>
                    Apply
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  size="large"
                  onClick={() =>
                    onSubmit?.({
                      startDate,
                      endDate,
                      id: filterKey,
                    })
                  }
                >
                  Apply
                </Button>
              )}
            </Stack>
          </Stack>
        </div>
      </div>
    </div>
  );
};
